import {gql} from '@apollo/client';
import {DocumentNode} from 'graphql';

import {assert} from 'Asserts';
import {ProcessTaskStatusType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {getAccessibleOrInaccessibleId, isInaccessible} from 'graphql-client/InaccessibleInfo';
import * as DateUtil from 'util/DateUtil';

import {
  ProcessDevelopmentStudyExecutionProgressQuery_study_StudyNode_procedureRuns_ProcedureRunNode_templateProcedureFlowchartVersion_ProcedureFlowchartVersionNode_nodes_ProcedureMethodDefinitionVersionNode as ProcedureMethodDefinitionVersionNode,
  ProcessDevelopmentStudyExecutionProgressQuery_study_StudyNode_procedureRuns_ProcedureRunNode_methodExecutionInstances_ProcedureMethodExecutionInstanceNode as ProcedureMethodExecutionInstanceNode,
  ProcessDevelopmentStudyExecutionProgressQuery_study_StudyNode_procedureRuns_ProcedureRunNode as ProcedureRun,
  ProcessDevelopmentStudyExecutionProgressQuery_study_StudyNode_procedureRuns_ProcedureRunNode_conditions_ProcedureRunConditionNode as ProcedureRunConditionNode,
  ProcessDevelopmentStudyExecutionProgressQuery_study_StudyNode_procedureRuns_ProcedureRunNode_conditions_ProcedureRunConditionNode_procedureRunConditionReplicates_ProcedureRunConditionReplicateNode as ProcedureRunConditionReplicateNode,
} from './__generated/ProcessDevelopmentStudyExecutionProgressQuery';

export interface ProcedureRunOverview {
  procedureRunId: string;
  displayId: string | null;
  name: string;
  procedureTemplateId: string;
  createdAt: string;
  unitOperationMethods: Array<UnitOperationMethod>;
  conditions: Array<Condition>;
  unitOperationExecutionInstances: Array<UnitOperationExecutionInstance>;
}

export interface UnitOperationMethod {
  id: string;
  name: string;
}

export interface Condition {
  id: string;
  name: string;
  description: string;
  replicates: Array<Replicate>;
}

export interface Replicate {
  id: string;
  replicateNumber: number;
  processRootTaskId: string | null;
}

export interface UnitOperationExecutionInstance {
  id: string;
  methodId: string | null;
  conditionId: string;
  replicateNumber: number;
  worksheetId: string | null;
  worksheetCreatedAt: string | null;
  worksheetUrl: string | null;
  status: TaskStatus | null;
  linkedUnitOperationExecutionInstanceIds: Array<string>;
}

export interface TaskStatus {
  id: string;
  statusType: ProcessTaskStatusType;
}

export type PLANNED = 'PLANNED';
export const PLANNED: PLANNED = 'PLANNED';
export type UnitOperationStatusType = ProcessTaskStatusType | PLANNED;

export const PROCESS_DEVELOPMENT_STUDY_EXECUTION_PROGRESS_QUERY: DocumentNode = gql`
  query ProcessDevelopmentStudyExecutionProgressQuery($studyId: StrictString!)
  @skipAudit
  @engComponent(name: STUDIES) {
    study(id: $studyId) {
      ... on StudyNode {
        id
        studySchema {
          id
          studyType
        }
        phase
        procedureRuns {
          ... on ProcedureRunNode {
            id
            name
            displayId
            createdAt
            templateProcedureFlowchartVersion {
              ... on ProcedureFlowchartVersionNode {
                id
                nodes {
                  id
                  name
                }
                procedureFlowchart {
                  ... on ProcedureFlowchartNode {
                    id
                    parent {
                      id
                    }
                  }
                }
              }
              ...InaccessibleInfoGraphQL
            }
            conditions {
              id
              name
              description
              procedureRunConditionReplicates {
                id
                replicateNumber
                processRootTask {
                  ... on ProcessTaskNode {
                    id
                  }
                  ...InaccessibleInfoGraphQL
                }
              }
            }
            methodExecutionInstances {
              id
              templateMethodVersion {
                ... on ProcedureMethodDefinitionVersionNode {
                  id
                }
                ...InaccessibleInfoGraphQL
              }
              sourceProcedureMethodExecutionInstanceAssociations {
                ... on ProcedureMethodExecutionInstanceAssociationNode {
                  id
                  sourceProcedureMethodExecutionInstance {
                    ... on ProcedureMethodExecutionInstanceNode {
                      id
                    }
                  }
                }
                ...InaccessibleInfoGraphQL
              }
              procedureRunConditionReplicate {
                id
                replicateNumber
                procedureRunCondition {
                  id
                }
              }
              worksheet {
                ... on WorksheetNode {
                  id
                  name
                  createdAt
                  editUrl
                }
                ...InaccessibleInfoGraphQL
              }
              methodTask {
                ... on ProcessTaskNode {
                  id
                  status {
                    ... on ProcessTaskStatusNode {
                      id
                      statusType
                    }
                  }
                }
                ...InaccessibleInfoGraphQL
              }
            }
          }
          ...InaccessibleInfoGraphQL
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

/**
 * Calculates the count of unit operations by task status.
 *
 * @param overview - The procedure run overview.
 * @returns A map containing the count of unit operations for each task status. If the status is not included in the map, we can assume that there are no unit operations with that status.
 */
export function getUnitOperationCountByTaskStatus(
  overview: ProcedureRunOverview
): Map<UnitOperationStatusType, number> {
  return overview.unitOperationExecutionInstances.reduce((counts, instance) => {
    const statusType: UnitOperationStatusType = instance.status?.statusType ?? PLANNED;
    counts.set(statusType, (counts.get(statusType) ?? 0) + 1);
    return counts;
  }, new Map<UnitOperationStatusType, number>());
}

/**
 * Builds the process development study execution progress overview based on the procedure run data.
 *
 * @param procedureRun - The procedure run data.
 * @returns The process development study execution progress overview.
 */
export function buildProcessDevelopmentStudyExecutionProgress(
  procedureRun: ProcedureRun
): ProcedureRunOverview {
  // Build unit operation methods
  const unitOperationMethods: Array<UnitOperationMethod> = isInaccessible(
    procedureRun.templateProcedureFlowchartVersion
  )
    ? []
    : procedureRun.templateProcedureFlowchartVersion.nodes.map(
        (method: ProcedureMethodDefinitionVersionNode) => ({
          id: method.id,
          name: method.name,
        })
      );

  // Build conditions
  const conditions: Array<Condition> = procedureRun.conditions.map(
    (condition: ProcedureRunConditionNode) => ({
      id: condition.id,
      name: condition.name,
      description: condition.description,
      replicates: condition.procedureRunConditionReplicates.map(
        (replicate: ProcedureRunConditionReplicateNode) => ({
          id: replicate.id,
          replicateNumber: replicate.replicateNumber,
          processRootTaskId:
            replicate.processRootTask != null
              ? getAccessibleOrInaccessibleId(replicate.processRootTask)
              : null,
        })
      ),
    })
  );

  // Build unit operation execution instances
  const unitOperationExecutionInstances: Array<UnitOperationExecutionInstance> =
    procedureRun.methodExecutionInstances.map(
      (methodExecutionInstance: ProcedureMethodExecutionInstanceNode) => ({
        id: methodExecutionInstance.id,
        methodId: isInaccessible(methodExecutionInstance.templateMethodVersion)
          ? null
          : methodExecutionInstance.templateMethodVersion.id,
        conditionId: methodExecutionInstance.procedureRunConditionReplicate.procedureRunCondition.id,
        replicateNumber: methodExecutionInstance.procedureRunConditionReplicate.replicateNumber,
        worksheetId:
          methodExecutionInstance.worksheet == null || isInaccessible(methodExecutionInstance.worksheet)
            ? null
            : methodExecutionInstance.worksheet.id,
        worksheetCreatedAt:
          methodExecutionInstance.worksheet == null || isInaccessible(methodExecutionInstance.worksheet)
            ? null
            : DateUtil.readableTime(methodExecutionInstance.worksheet.createdAt, {showTime: true}),
        worksheetUrl:
          methodExecutionInstance.worksheet == null || isInaccessible(methodExecutionInstance.worksheet)
            ? null
            : methodExecutionInstance.worksheet.editUrl,
        status:
          methodExecutionInstance.methodTask == null || isInaccessible(methodExecutionInstance.methodTask)
            ? null
            : {
                id: methodExecutionInstance.methodTask.id,
                statusType: methodExecutionInstance.methodTask.status.statusType,
              },
        linkedUnitOperationExecutionInstanceIds:
          methodExecutionInstance.sourceProcedureMethodExecutionInstanceAssociations == null
            ? []
            : methodExecutionInstance.sourceProcedureMethodExecutionInstanceAssociations.map(
                (sourceProcedureMethodExecutionInstanceAssociation) =>
                  isInaccessible(sourceProcedureMethodExecutionInstanceAssociation)
                    ? sourceProcedureMethodExecutionInstanceAssociation.invalidId
                    : sourceProcedureMethodExecutionInstanceAssociation.sourceProcedureMethodExecutionInstance
                        .id
              ),
      })
    );

  // During the initial creation of the study, the Process Tasks are generated asynchronously
  // If there are any first method operations without tasks, we can pre-populate them to PENDING
  const firstMethodId = unitOperationMethods[0]?.id;
  if (firstMethodId) {
    unitOperationExecutionInstances.forEach((instance) => {
      if (instance.methodId === firstMethodId && instance.status == null) {
        instance.status = {id: '', statusType: ProcessTaskStatusType.PENDING};
      }
    });
  }

  // In client/studies/typed/process-development/MultiExperimentOverview.tsx, we filter out any procedureRuns
  // that have inaccessible template procedure flowchart versions
  assert(
    !isInaccessible(procedureRun.templateProcedureFlowchartVersion),
    'Expected template procedure flowchart version to be accessible.'
  );
  // Procedure flowchart parent is null when the type is procedure run, but we expect it to be of type
  // procedure here as the procedure holds the template
  assert(
    procedureRun.templateProcedureFlowchartVersion.procedureFlowchart?.parent != null,
    'Expected procedure flowchart parent to be non-null.'
  );

  // Build the overview object
  const overview: ProcedureRunOverview = {
    procedureRunId: procedureRun.id,
    name: procedureRun.name,
    displayId: procedureRun.displayId,
    procedureTemplateId: procedureRun.templateProcedureFlowchartVersion.procedureFlowchart.parent.id,
    createdAt: procedureRun.createdAt,
    unitOperationMethods,
    conditions,
    unitOperationExecutionInstances,
  };

  return overview;
}
